import { FC, useEffect, useState } from 'react';

import CopyButton from '@/components/CopyButton';
import CollapsibleArea from '@/components/CollapsibleArea';
import { Table, THead, TBody, TableRow, TH, TD } from '@/components/DesignLibrary/Table';
import useNoodleApi from '@/hooks/useNoodleApi';
import getDataReferences from '@/tsClient/questionnaires/getDataReferences';
import { DataReference } from '@/typings/questionnaires-api-models';
import { Select } from '@/components/DesignLibrary/Atoms';
import Spacer from '@/components/Spacer';

import s from './DynamicForm.module.scss';

interface TableRowData {
  [key: string]: string | number | boolean | null | undefined;
  stateOfFilingFilter?: string;
}

const ExternalData: FC = () => {
  const [data, setData] = useState<DataReference[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState('');
  const [stateOptions, setStateOptions] = useState<{ key: string; label: string }[]>([]);
  const { getData: getDataReferencesFn } = useNoodleApi(getDataReferences);

  useEffect(() => {
    const fetchDatasets = async (): Promise<void> => {
      try {
        const response = await getDataReferencesFn({
          page: 1,
          perPage: 100_000,
        });
        if (response) {
          setData(response.data?.items);
          const states = new Set<string>();
          response.data?.items.forEach(set => {
            if (typeof set.data !== 'string') return;
            try {
              const parsedData = JSON.parse(set.data);
              if (parsedData?.table && Array.isArray(parsedData.table)) {
                parsedData.table.forEach((row: TableRowData) => {
                  if (row.stateOfFilingFilter) {
                    states.add(row.stateOfFilingFilter);
                  }
                });
              }
            } catch (error) {
              console.error('Error parsing dataset:', error);
            }
          });
          setStateOptions(
            Array.from(states)
              .sort()
              .map(state => ({ key: state, label: state })),
          );
        }
      } catch (error) {
        console.error('Error fetching datasets:', error);
      }
    };

    fetchDatasets();
  }, [getDataReferencesFn]);

  useEffect(() => {
    if (stateOptions.length > 0) {
      setSelectedState(stateOptions[0].key);
    }
  }, [stateOptions]);

  return (
    <>
      {stateOptions.length > 0 && (
        <>
          <Select
            label="State of filing"
            options={stateOptions}
            value={selectedState}
            onChange={value => setSelectedState(value)}
            placeholder="Select a state"
            compact
            optional
          />
          <Spacer />
        </>
      )}

      {data?.map((set: DataReference) => {
        if (typeof set.data !== 'string') return null;

        try {
          const parsedData = JSON.parse(set.data);
          if (!parsedData?.table || !Array.isArray(parsedData.table)) return null;

          const hasStateFilter = parsedData.table.some((row: TableRowData) => 'stateOfFilingFilter' in row);

          let tableData: TableRowData[] = parsedData.table;
          let shouldRender = true;

          if (hasStateFilter) {
            if (selectedState) {
              tableData = tableData.filter(row => row.stateOfFilingFilter === selectedState);
            }
            shouldRender = tableData.length > 0 || !selectedState;
          }

          if (!shouldRender) return null;

          // Exclude "state" columns
          const headers = (tableData.length > 0 && stateOptions.length > 0) ? Object.keys(tableData[0]).filter(header => (hasStateFilter ? (header !== 'stateOfFilingFilter' && header !== 'state' && header !== 'State Name') : true)) : [];

          return (
            <CollapsibleArea title={set.label.startsWith("State Exemptions") ? "State Exemptions" : set.label} key={set.slug} className={s.collapsibleDataSet}>
              <div className={s.dataTableWrapper}>
                <Table>
                  <THead>
                    <TableRow>
                      {headers.map((header: string) => (
                        <TH key={header} label={header} />
                      ))}
                    </TableRow>
                  </THead>
                  <TBody>
                    {tableData.map((row: TableRowData, rowIndex: number) => (
                      <TableRow key={rowIndex}>
                        {headers.map((header: string) => (
                          <TD key={`${rowIndex}-${header}`}>
                            <CopyButton isWrapper className={s.copyButton} text={String(row[header])} title="Click to copy">
                              {String(row[header])}
                            </CopyButton>
                          </TD>
                        ))}
                      </TableRow>
                    ))}
                  </TBody>
                </Table>
              </div>
            </CollapsibleArea>
          );
        } catch (error) {
          console.error('Error parsing dataset:', error);
          return null;
        }
      })}
    </>
  );
};

export default ExternalData;
